import { QuestionTypes } from '@showbie-socrative/socrative-utils';
import { normalize } from 'normalizr';
import { useDispatch } from 'react-redux';

import { QuestionSavedEventAdapter } from '~analytics/adapters/QuestionSavedEventAdapter';
import { useQuizQuestionUrl } from '~teacher/components/QuizEditor/contexts/QuizQuestionUrlProvider';
import { useCreateQuestion } from '~teacher/hook/question/useCreateQuestion';
import { QuizQuestionSchema } from '~teacher/models/quizzes';
import {
  addQuestionToQuiz,
  changeStatusToOk,
  changeStatusToSaving,
  setQuizAnswers,
  setQuizQuestions,
  updateQuizQuestionOrder,
} from '~teacher/slices/quiz/quizSlice';

interface UseAddQuestionParams {
  quizId: number;
  position: number;
  onQuestionAdded?: (questionId: string) => void;
}

export interface AddQuestionParams {
  questionId: string;
  questionType: QuestionTypes;
  questionText: string;
  answers: { text: string; isCorrect: boolean; order: number }[];
  explanation: string;
}

export function useAddQuestion({
  quizId,
  position,
  onQuestionAdded,
}: UseAddQuestionParams) {
  const quizQuestionUrl = useQuizQuestionUrl();
  const createQuestion = useCreateQuestion();
  const dispatch = useDispatch();

  const addQuestion = async ({
    questionType,
    questionText,
    answers,
    questionId,
    explanation,
  }: AddQuestionParams) => {
    dispatch(changeStatusToSaving());

    try {
      const apiResponse = await createQuestion.mutateAsync({
        questionText: questionText,
        quizId: quizId,
        type: questionType,
        explanation: explanation,
        order: position,
        quizQuestionUrl: quizQuestionUrl,
        answers: answers.map((answer) => ({
          questionId: null,
          id: null,
          createdById: null,
          imageUrl: null,
          image: null,
          resources: null,
          text: answer.text,
          isCorrect: answer.isCorrect,
          order: answer.order,
        })),
      });
      const { question, orders } = apiResponse;
      const result = normalize(question, QuizQuestionSchema);
      const {
        entities: { quizQuestion: newQuestions, quizAnswer: newAnswers },
      } = result;
      // Save the questions
      dispatch(setQuizQuestions({ questions: newQuestions }));
      // Save the answers
      Object.values(newQuestions).forEach((quizQuestion: any) => {
        const { answers: questionAnswers, type } = quizQuestion;
        const answersToSet = questionAnswers.reduce(
          (aggregator: any, answerId: any) => {
            aggregator[answerId] = newAnswers[answerId];
            return aggregator;
          },
          {}
        );
        dispatch(
          setQuizAnswers({
            answers: answersToSet,
            questionType: type,
          })
        );
      });
      // Add references to questions in quiz
      dispatch(
        addQuestionToQuiz({
          quizId: quizId,
          questionId: question.questionId,
        })
      );
      // Make sure the question order is proper
      dispatch(updateQuizQuestionOrder({ questionOrderMap: orders }));
      // We did okay!
      dispatch(changeStatusToOk());
      // Analytics
      new QuestionSavedEventAdapter({
        originalQuestion: null,
        originalAnswers: null,
        modifiedQuestion: question,
        modifiedAnswers: Object.values(newAnswers ?? {}),
        generationSource: 'AI',
      }).sendAnalytics();

      // Mark as added after successful creation
      onQuestionAdded?.(questionId);
    } catch (error) {
      dispatch(changeStatusToOk());
      throw error;
    }
  };

  return { addQuestion };
}
